import { useResource, titleCase } from "../../lib/hooks";
import { STAGES, Lead } from "./Pipeline";
import type { Referral } from "../../lib/types";

interface Inquiry {
    id: string;
    status: string;
    created_at: string;
}

const currency = (n: number): string =>
    n.toLocaleString("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 });

/** A horizontal bar with its value — the same meter kit the dashboards use. */
const Bar = ({ label, value, max, display, tone }: {
    label: string; value: number; max: number; display: string; tone?: string;
}) => (
    <div className="person-row">
        <div className="person-main">
            <div className="name">{label}</div>
        </div>
        <div className="meter" style={{ width: 220 }}>
            <div className="meter-track">
                <div className="meter-fill" style={{
                    width: `${max === 0 ? 0 : Math.round((value / max) * 100)}%`,
                    background: tone ?? "var(--accent)",
                }} />
            </div>
            <span className="meter-value" style={{ minWidth: 76 }}>{display}</span>
        </div>
    </div>
);

/**
 * Pipeline analytics, computed client-side from the collections already loaded.
 * At this data volume a reporting endpoint would be premature — and every
 * figure here is derived from rows the viewer's role is allowed to see, because
 * the queries behind them ran under RLS like everything else.
 */
export const Analytics = () => {
    const leads = useResource<Lead[]>("/leads");
    const referrals = useResource<Referral[]>("/referrals");
    const inquiries = useResource<Inquiry[]>("/inquiries");

    const all = leads.data ?? [];
    const won = all.filter((l) => l.stage === "closed_won");
    const lost = all.filter((l) => l.stage === "closed_lost");
    const closed = won.length + lost.length;
    const winRate = closed === 0 ? 0 : Math.round((won.length / closed) * 100);

    const valueByStage = STAGES.map((stage) => ({
        stage,
        value: all.filter((l) => l.stage === stage.id)
            .reduce((sum, l) => sum + Number(l.estimated_value ?? 0), 0),
        count: all.filter((l) => l.stage === stage.id).length,
    }));
    const maxValue = Math.max(...valueByStage.map((v) => v.value), 1);

    const referralByStatus = ["new", "in_progress", "allocated", "active", "completed", "closed"]
        .map((status) => ({
            status,
            count: (referrals.data ?? []).filter((r) => r.status === status).length,
        }))
        .filter((r) => r.count > 0);
    const maxReferral = Math.max(...referralByStatus.map((r) => r.count), 1);

    if (leads.error) return <div className="error-banner">{leads.error}</div>;

    return (
        <div style={{ display: "grid", gap: "1.1rem" }}>
            <div className="grid cols-4">
                <div className="card stat">
                    <div className="label">Won</div>
                    <div className="value" style={{ color: "var(--pass)" }}>{won.length}</div>
                    <div className="sub">{currency(won.reduce((s, l) => s + Number(l.estimated_value ?? 0), 0))}</div>
                </div>
                <div className="card stat">
                    <div className="label">Lost</div>
                    <div className="value" style={{ color: "var(--block)" }}>{lost.length}</div>
                </div>
                <div className="card stat">
                    <div className="label">Win rate</div>
                    <div className="value">{winRate}%</div>
                    <div className="sub">of {closed} closed</div>
                </div>
                <div className="card stat">
                    <div className="label">Inquiries</div>
                    <div className="value">{(inquiries.data ?? []).length}</div>
                    <div className="sub">{(inquiries.data ?? []).filter((i) => i.status === "converted").length} converted</div>
                </div>
            </div>

            <div className="grid cols-2">
                <div className="card">
                    <div className="card-head"><h2>Pipeline value by stage</h2></div>
                    <div className="card-body">
                        {valueByStage.map(({ stage, value, count }) => (
                            <Bar key={stage.id}
                                label={`${stage.label} (${count})`}
                                value={value} max={maxValue}
                                display={value > 0 ? currency(value) : "—"}
                                tone={stage.id === "closed_won" ? "var(--pass)"
                                    : stage.id === "closed_lost" ? "var(--block)" : "var(--accent)"} />
                        ))}
                    </div>
                </div>

                <div className="card">
                    <div className="card-head"><h2>Referrals by status</h2></div>
                    <div className="card-body">
                        {referralByStatus.length === 0 ? (
                            <p style={{ color: "var(--muted)", fontSize: "0.85rem" }}>No referrals recorded.</p>
                        ) : referralByStatus.map(({ status, count }) => (
                            <Bar key={status}
                                label={titleCase(status)} value={count} max={maxReferral}
                                display={String(count)} tone="var(--brand-accent)" />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
